"use client";

/**
 * Collapsible directory tree for nested deliverable file paths.
 * Top-level folders start open (Floor Plans / Interior Elevations /
 * RCP). Nested floors and rooms start closed so a 400-file drop
 * does not render as one scrolling string list.
 */

import {
  createContext,
  Fragment,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronDown, ChevronRight, Folder } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  BIM_FILE_TYPE_COUNT_CLASS,
} from "@/lib/virtualspace/bim-file-list-ui";
import {
  buildDeliverableFileFolderTree,
  type DeliverableFolderNode,
} from "@/lib/virtualspace/deliverable-file-folders";

export const FOLDER_TREE_ROW_BASE_PAD_PX = 12;
export const FOLDER_TREE_INDENT_PX = 14;

const FolderTreeLeafDepthContext = createContext<number | null>(null);

export function folderTreeFolderPaddingLeft(depth: number): number {
  return FOLDER_TREE_ROW_BASE_PAD_PX + depth * FOLDER_TREE_INDENT_PX;
}

/** Align file leaves with the next folder depth. */
export function folderTreeLeafPaddingLeft(depth: number): number {
  return folderTreeFolderPaddingLeft(depth + 1);
}

export function useFolderTreeLeafStyle(): CSSProperties | undefined {
  const depth = useContext(FolderTreeLeafDepthContext);
  if (depth == null) return undefined;
  return {
    paddingLeft: folderTreeLeafPaddingLeft(depth),
    paddingRight: FOLDER_TREE_ROW_BASE_PAD_PX,
  };
}
type Props<T> = {
  files: T[];
  getFileName: (file: T) => string | null | undefined;
  getRowKey: (file: T) => string;
  renderFile: (file: T) => ReactNode;
  /** Depth 0 = top-level folders open. Nested stay collapsed. */
  expandDepth?: number;
  listClassName?: string;
  showSharedPrefix?: boolean;
};

function FolderBranch<T>({
  node,
  depth,
  expandDepth,
  getRowKey,
  renderFile,
}: {
  node: DeliverableFolderNode<T>;
  depth: number;
  expandDepth: number;
  getRowKey: (file: T) => string;
  renderFile: (file: T) => ReactNode;
}) {
  const [open, setOpen] = useState(depth <= expandDepth);
  const hasBody = node.folders.length > 0 || node.files.length > 0;

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => hasBody && setOpen((value) => !value)}
        className={cn(
          "flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50",
          hasBody ? "" : "cursor-default",
        )}
        style={{ paddingLeft: folderTreeFolderPaddingLeft(depth) }}
        aria-expanded={hasBody ? open : undefined}
        aria-label={`${node.name}, ${node.fileCount} file${node.fileCount === 1 ? "" : "s"}`}
      >
        <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-muted-foreground">
          {hasBody ? (
            open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )
          ) : null}
        </span>
        <Folder className="h-3.5 w-3.5 shrink-0 text-sky-700/80 dark:text-sky-400/80" />
        <span className="min-w-0 flex-1 truncate font-medium" title={node.path}>
          {node.name}
        </span>
        <span className={BIM_FILE_TYPE_COUNT_CLASS}>
          {node.fileCount} file{node.fileCount === 1 ? "" : "s"}
        </span>
      </button>
      {open && hasBody ? (
        <ul className="divide-y vs-border-soft">
          {node.folders.map((child) => (
            <FolderBranch
              key={child.path}
              node={child}
              depth={depth + 1}
              expandDepth={expandDepth}
              getRowKey={getRowKey}
              renderFile={renderFile}
            />
          ))}
          {node.files.map((file) => (
            <FileLeaf key={getRowKey(file)} depth={depth}>
              {renderFile(file)}
            </FileLeaf>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function FileLeaf({
  children,
  depth,
}: {
  children: ReactNode;
  depth: number;
}) {
  return (
    <FolderTreeLeafDepthContext.Provider value={depth}>
      {children}
    </FolderTreeLeafDepthContext.Provider>
  );
}

export default function BimCadFolderTree<T>({
  files,
  getFileName,
  getRowKey,
  renderFile,
  expandDepth = 0,
  listClassName = "divide-y vs-border-soft rounded-md border vs-border-soft",
  showSharedPrefix = true,
}: Props<T>) {
  const tree = useMemo(
    () => buildDeliverableFileFolderTree(files, getFileName),
    [files, getFileName],
  );

  if (tree.roots.length === 0 && tree.looseFiles.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      {showSharedPrefix && tree.sharedPrefix ? (
        <p className="px-1 text-[11px] text-muted-foreground truncate" title={tree.sharedPrefix}>
          From {tree.sharedPrefix}
        </p>
      ) : null}
      <ul className={listClassName}>
        {tree.roots.map((node) => (
          <FolderBranch
            key={node.path}
            node={node}
            depth={0}
            expandDepth={expandDepth}
            getRowKey={getRowKey}
            renderFile={renderFile}
          />
        ))}
        {tree.looseFiles.map((file) => (
          <Fragment key={getRowKey(file)}>{renderFile(file)}</Fragment>
        ))}
      </ul>
    </div>
  );
}
